/**
 * Simulation Replay Page
 *
 * Step-by-step replay of a simulation run with state inspector,
 * LLM calls panel, and tool calls panel.
 * Route: /simulation/runs/:id/replay
 */
import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  CpuChipIcon,
  WrenchScrewdriverIcon,
  ClockIcon,
} from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { simulationStore } from '@/stores/SimulationStore'
import { useState } from 'react'

function getStatusBadgeVariant(status: string): 'primary' | 'success' | 'warning' | 'error' | 'gray' {
  const variants: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'gray'> = {
    completed: 'success',
    failed: 'error',
    running: 'primary',
    skipped: 'gray',
  }
  return variants[status] || 'gray'
}

function formatDuration(ms?: number | null) {
  if (!ms) return 'N/A'
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}

function CollapsibleJson({ title, data, defaultOpen }: {
  title: string
  data: any
  defaultOpen?: boolean
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen ?? false)

  if (!data || (Array.isArray(data) && data.length === 0) || (typeof data === 'object' && Object.keys(data).length === 0)) {
    return null
  }

  return (
    <div className="border border-border rounded-lg">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full px-4 py-2 hover:bg-muted/50 text-left"
      >
        {isOpen ? (
          <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
        )}
        <span className="text-sm font-medium text-foreground">{title}</span>
      </button>
      {isOpen && (
        <div className="px-4 pb-3">
          <pre className="bg-muted/50 border border-border rounded p-3 text-xs overflow-x-auto max-h-64">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  )
}

const SimulationReplayPage = observer(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  useEffect(() => {
    if (!id) return
    simulationStore.loadReplay(id)

    return () => {
      simulationStore.clearReplay()
    }
  }, [id])

  const replay = simulationStore.replayData
  const step = simulationStore.currentStep
  const stepIndex = simulationStore.currentStepIndex
  const totalSteps = replay?.total_steps ?? 0

  if (simulationStore.replayLoading) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/simulation/runs/${id}`)}>
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          Back to Run
        </Button>
        <Card className="animate-pulse">
          <div className="h-64 bg-muted rounded" />
        </Card>
      </div>
    )
  }

  if (!replay || totalSteps === 0) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/simulation/runs/${id}`)}>
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          Back to Run
        </Button>
        <Card className="text-center py-12">
          <h3 className="text-lg font-semibold text-foreground mb-2">No replay data available</h3>
          <p className="text-muted-foreground">This run does not have step-by-step replay data.</p>
        </Card>
      </div>
    )
  }

  const progressPercent = totalSteps > 1 ? (stepIndex / (totalSteps - 1)) * 100 : 100

  return (
    <div className="space-y-6">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={() => navigate(`/simulation/runs/${id}`)}>
        <ArrowLeftIcon className="w-4 h-4 mr-1" />
        Back to Run
      </Button>

      {/* Header */}
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-foreground">
              Replay: Run {id?.slice(0, 8)}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Step {stepIndex + 1} of {totalSteps}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </Card>

      {/* Step Controls */}
      <Card>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => simulationStore.stepBack()}
              disabled={stepIndex === 0}
            >
              <ChevronLeftIcon className="w-4 h-4 mr-1" />
              Back
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={() => simulationStore.stepForward()}
              disabled={stepIndex >= totalSteps - 1}
            >
              Forward
              <ChevronRightIcon className="w-4 h-4 ml-1" />
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Jump to step:</label>
            <select
              value={stepIndex}
              onChange={(e) => simulationStore.setReplayStep(Number(e.target.value))}
              className="px-3 py-1.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {replay.steps.map((s, idx) => (
                <option key={idx} value={idx}>
                  {idx + 1}: {s.node_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {step && (
        <>
          {/* Current Step Display */}
          <Card>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-500/15 rounded-lg">
                <ClockIcon className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-foreground">{step.node_name}</h2>
                <div className="flex items-center gap-3 mt-1">
                  <Badge variant={getStatusBadgeVariant(step.status)} size="sm">
                    {step.status}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Duration: {formatDuration(step.duration_ms)}
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* State Inspector */}
          <div className="space-y-3">
            <h3 className="text-md font-semibold text-foreground">State Inspector</h3>
            <CollapsibleJson title="Input State" data={step.input_state} defaultOpen />
            <CollapsibleJson title="Output State" data={step.output_state} defaultOpen />
          </div>

          {/* LLM Calls Panel */}
          {step.llm_calls && step.llm_calls.length > 0 && (
            <Card>
              <h3 className="text-md font-semibold text-foreground mb-3 flex items-center gap-2">
                <CpuChipIcon className="w-5 h-5 text-blue-600" />
                LLM Calls ({step.llm_calls.length})
              </h3>
              <div className="space-y-3">
                {step.llm_calls.map((call: any, idx: number) => (
                  <div key={idx} className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-600">
                        Call {idx + 1}
                      </span>
                      {call.model && (
                        <Badge variant="primary" size="sm">{call.model}</Badge>
                      )}
                    </div>
                    <pre className="text-xs overflow-x-auto max-h-48">
                      {JSON.stringify(call, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Tool Calls Panel */}
          {step.tool_calls && step.tool_calls.length > 0 && (
            <Card>
              <h3 className="text-md font-semibold text-foreground mb-3 flex items-center gap-2">
                <WrenchScrewdriverIcon className="w-5 h-5 text-green-600" />
                Tool Calls ({step.tool_calls.length})
              </h3>
              <div className="space-y-3">
                {step.tool_calls.map((call: any, idx: number) => (
                  <div key={idx} className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-green-600">
                        {call.tool_name || `Tool ${idx + 1}`}
                      </span>
                      {call.status && (
                        <Badge
                          variant={call.status === 'success' ? 'success' : 'error'}
                          size="sm"
                        >
                          {call.status}
                        </Badge>
                      )}
                    </div>
                    <pre className="text-xs overflow-x-auto max-h-48">
                      {JSON.stringify(call, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  )
})

export default SimulationReplayPage
